import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Plx from 'react-plx'
import Vertical from './vertical'
import VerticalMenu from './vertical-menu'
import { Player, BigPlayButton, ControlBar, Shortcut } from 'video-react'

const verticalsParallax = [
  {
    start: '.verticals',
    end: '100%',
    properties: [
      {
        startValue: 0,
        endValue: -60,
        property: 'translateY',
        unit: '%'
      }
    ]
  }
]

const generateUniqueRef = (prefix = 'ref') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
}

class VerticalList extends Component {
  constructor (props) {
    super(props)

    this.state = {
      activeVertical: 0,
      isMobile: false,
      isIDevice: false,
      markerPosition: 0
    }

    this.fullVideo = generateUniqueRef('fullVideo')
    this.fullVideoPlayer = generateUniqueRef('fullVideoPlayer')

    this.checkWidth = this.checkWidth.bind(this)
    this.changeVertical = this.changeVertical.bind(this)
    this.showNextVertical = this.showNextVertical.bind(this)
    this.handleFullVideoState = this.handleFullVideoState.bind(this)
    this.handleWatchNowClick = this.handleWatchNowClick.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleModalCloseClick = this.handleModalCloseClick.bind(this)
  }

  checkWidth () {
    // This method and the event listeners below exist so that the layout is
    // responsive to browser resizing across all breakpoints
    const width = typeof window !== 'undefined' ? window.innerWidth : 0
    this.setState({ isMobile: width < 1024 })
  }

  componentWillMount () {
    // TODO: find a more robust solution for mobile vs desktop detection
    // Matches our desktop breakpoint value
    typeof window !== 'undefined' && window.addEventListener('resize', this.checkWidth)
    this.checkWidth()
  }

  componentDidMount () {
    const isIDevice = window.navigator.userAgent.match(/iPhone|iPad|iPod/i)
    const fullVideo = this.refs[this.fullVideo]
    const fullVideoPlayer = this.refs[this.fullVideoPlayer]

    this.setState({
      isIDevice
    })

    if (fullVideoPlayer) {
      fullVideoPlayer.subscribeToStateChange(this.handleFullVideoState)
    }

    if (isIDevice && fullVideo) {
      fullVideo.querySelector('video').addEventListener(
        'webkitendfullscreen', this.handleModalCloseClick)
    }

    this.intervalId = setInterval(this.showNextVertical, 10000)
  }

  componentWillUnmount () {
    const fullVideo = this.refs[this.fullVideo]
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    if (this.state.isIDevice && fullVideo) {
      fullVideo.querySelector('video').removeEventListener(
        'webkitendfullscreen', this.handleModalCloseClick)
    }

    typeof window !== 'undefined' && window.removeEventListener('resize', this.checkWidth)
  }

  changeVertical (event) {
    event.preventDefault()
    const verticalId = event.target.dataset.vertical

    this.setState(
      { activeVertical: Number(verticalId) },
      () => this.updateMarkerPosition()
    )

    // Clear the timer since the user has interacted with the menu
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  }

  updateMarkerPosition () {
    const activeItem = document.querySelector('.vertical-menu-item.is-active')
    if (activeItem) {
      this.setState({ markerPosition: activeItem.offsetTop })
    }
  }

  showNextVertical () {
    const { verticals } = this.props
    let nextVertical = this.state.activeVertical + 1

    // Reset if next vertical id isn't found
    if (nextVertical >= verticals.length) {
      nextVertical = 0
    }

    this.setState(
      { activeVertical: nextVertical },
      () => this.updateMarkerPosition()
    )
  }

  handleFullVideoState ({ ended, isFullscreen, paused, userActivity }) {
    if (ended && !isFullscreen) {
      this.handleModalCloseClick()

      if (this.state.isIDevice) {
        this.refs[this.fullVideo].querySelector('video').webkitExitFullscreen()
      }
    }

    if (userActivity) {
      document.querySelector('.modal--close').classList.remove('hide')
    } else if (!paused) {
      document.querySelector('.modal--close').classList.add('hide')
    }
  }

  handleWatchNowClick () {
    const fullVideo = this.refs[this.fullVideo]
    const fullVideoPlayer = this.refs[this.fullVideoPlayer]

    // Clear the timer since there is a user interaction
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    fullVideo.parentElement.classList.add('is-active')
    document.querySelector('body').classList.add('fullvideo--open')

    fullVideoPlayer.play()
  }

  handleModalCloseClick () {
    const fullVideo = this.refs[this.fullVideo]
    const fullVideoPlayer = this.refs[this.fullVideoPlayer]
    fullVideo.parentElement.classList.remove('is-active')
    document.querySelector('body').classList.remove('fullvideo--open')

    fullVideoPlayer.pause()
    fullVideoPlayer.load()
  }

  handleKeyDown (event) {
    event.preventDefault()

    if (event.key === 'Enter') {
      this.handleModalCloseClick()
    }
  }

  render () {
    const { verticals } = this.props
    const { isMobile, activeVertical } = this.state

    return (
      <Plx
        disabled
        parallaxData={verticalsParallax}
        className='verticals__parallax'
      >
        <section className='verticals'>
          <div className='wrapper--max-width'>
            <ul className='vertical-list ul--reset'>
              {verticals.map((item, index) => {
                const vertical = item.node
                return (
                  <Vertical
                    activeVertical={activeVertical}
                    key={vertical.id}
                    index={index}
                    vertical={vertical}
                    isMobile={isMobile}
                    handleClick={this.handleWatchNowClick}
                  />
                )
              })}
            </ul>
            <VerticalMenu
              activeVertical={this.state.activeVertical}
              verticals={verticals}
              onVerticalClick={this.changeVertical}
              isMobile={isMobile}
              markerPosition={this.state.markerPosition}
            />
          </div>
          <div className='verticals__fullvideo'>
            <div className='video-wrapper' ref={this.fullVideo}>
              <Player
                ref={this.fullVideoPlayer}
                className='video'
                aspectRatio='16:9'
                loop={false}
                playsInline
                preload='none'
                src={verticals[this.state.activeVertical].node.cta.url.path}
              >
                <BigPlayButton disabled />
                <ControlBar />
                <Shortcut />
              </Player>
              <div
                className='modal--close'
                onClick={this.handleModalCloseClick}
                onKeyDown={this.handleKeyDown}
                tabIndex={0}
              />
            </div>
          </div>
        </section>
      </Plx>
    )
  }
}

VerticalList.propTypes = {
  verticals: PropTypes.array.isRequired
}

export default VerticalList



// WEBPACK FOOTER //
// ./src/components/vertical-list.js